/**
 * Shared routing helpers for picking the active BDG tier (primary vs
 * contingent) in a given death-order scenario. Used by both the diagram
 * resolver and the BDG form sync hook so they stay in lock-step.
 */

export interface BdgActiveTierContext {
  firstGrantorId: string;
  /** Individuals whose death triggers distribution (policy holders / owner). */
  triggeringClientIds: string[];
  /** True when any policy on the account has `survivorship=true`. */
  isSurvivorship: boolean;
  possiblePrimaryClientIds: string[];
}

/**
 * Primary clients presumed dead by the time the BDG trigger fires.
 * Survivorship and trigger-dies-second both imply both clients are dead.
 */
export function getDeceasedClientIdsAtTrigger(
  ctx: BdgActiveTierContext
): Set<string> {
  const triggerFiresAtSecondDeath =
    ctx.isSurvivorship || !ctx.triggeringClientIds.includes(ctx.firstGrantorId);
  return new Set(
    triggerFiresAtSecondDeath
      ? ctx.possiblePrimaryClientIds
      : [ctx.firstGrantorId]
  );
}

export interface BdgPrimaryRecipient {
  /**
   * True for the surviving-spouse sentinel. Resolved via the
   * caller-supplied `survivingSpouseId` so the sentinel reports as
   * deceased when nobody actually survives at trigger time.
   */
  isSurvivingSpouseSentinel: boolean;
  /** Individual recipient id; null for non-individuals and sentinels. */
  individualId: string | null;
  /**
   * Owner client ids when the recipient is a "looked-through" account
   * entity (retirement, individual/direct-holdings, or joint — see
   * T1-6171). Their assets belong to living owner(s), so the designation
   * should cascade to contingent once every owner has died. The recipient
   * stays alive while ANY owner survives, so it reports as deceased only
   * when every id here is in `deceasedClientIds`. Null/empty means "not a
   * looked-through entity" → never reports as dead, as before.
   */
  lookThroughOwnerIds?: string[] | null;
}

/**
 * Primary tier is unavailable when every recipient is presumed dead at
 * trigger time. Individuals are checked against `deceasedClientIds`;
 * surviving-spouse sentinels resolve to `survivingSpouseId` and are dead
 * when that id is null (no one survives) or itself deceased. Looked-through
 * account recipients (retirement / direct-holdings / joint) are dead only
 * once every owner has died. All other non-individual recipients (other
 * entities, orgs, testamentary entities) never report as dead.
 */
export function isPrimaryTierUnavailable(
  primaryRecipients: BdgPrimaryRecipient[],
  deceasedClientIds: Set<string>,
  survivingSpouseId: string | null
): boolean {
  if (primaryRecipients.length === 0) return true;
  return primaryRecipients.every((r) =>
    isPrimaryRecipientDeceased(r, deceasedClientIds, survivingSpouseId)
  );
}

/** Whether a single primary recipient is presumed dead at trigger time. */
function isPrimaryRecipientDeceased(
  recipient: BdgPrimaryRecipient,
  deceasedClientIds: Set<string>,
  survivingSpouseId: string | null
): boolean {
  if (recipient.isSurvivingSpouseSentinel) {
    return (
      survivingSpouseId === null || deceasedClientIds.has(survivingSpouseId)
    );
  }
  // Looked-through account: dead only when EVERY owner has died.
  if (
    recipient.lookThroughOwnerIds &&
    recipient.lookThroughOwnerIds.length > 0
  ) {
    return recipient.lookThroughOwnerIds.every((id) =>
      deceasedClientIds.has(id)
    );
  }
  return (
    !!recipient.individualId && deceasedClientIds.has(recipient.individualId)
  );
}

/**
 * Resolves the "surviving spouse" sentinel to a concrete primary-client
 * id for a given scenario: the not-first-decedent primary client in a
 * two-client household. Returns null for households where the concept
 * doesn't apply; callers should treat the sentinel as deceased in that
 * case. Survivorship and trigger-fires-at-second-death are handled
 * correctly because the returned id is then in `deceasedClientIds`.
 */
export function getSurvivingSpouseIdAtTrigger(
  possiblePrimaryClientIds: string[],
  firstGrantorId: string
): string | null {
  if (possiblePrimaryClientIds.length !== 2) return null;
  return possiblePrimaryClientIds.find((id) => id !== firstGrantorId) ?? null;
}

/**
 * Beneficiary tier that is missing from a BDG entity whose active tier
 * resolved empty. Drives the diagram's "Please specify a {tier}
 * beneficiary" warning in place of the generic not-fully-distributed copy.
 */
export type BdgMissingBeneficiaryTier = 'primary' | 'contingent';

/**
 * An empty primary tier is the most fundamental gap (the account has no
 * designated beneficiary), so it takes precedence. Otherwise, when the
 * contingent tier is the one that fires (every primary recipient has
 * predeceased at the trigger, so `useContingent` is set), an absent
 * contingent leaves the account undistributed.
 */
export function getMissingBeneficiaryTier(args: {
  hasPrimaryRecipient: boolean;
  hasContingentRecipient: boolean;
  useContingent: boolean;
}): BdgMissingBeneficiaryTier | undefined {
  if (!args.hasPrimaryRecipient) return 'primary';
  if (args.useContingent && !args.hasContingentRecipient) return 'contingent';
  return undefined;
}

export interface DecideActiveBdgTierArgs {
  /**
   * Primary tier recipients mapped to the tier-availability shape. The
   * caller resolves `recipientKind` (e.g. via `deriveRecipientKindFromId`)
   * before calling so this helper stays decoupled from form types.
   */
  primaryRecipientsForTierCheck: BdgPrimaryRecipient[];
  selectedPrimaryClientId: string;
  /**
   * Full list of clients whose death triggers distribution. The caller
   * derives this via `getBdgTriggerInfo` (which sees the entity's owner
   * / policy holders) — never re-derive it from a single
   * `defaultTriggerClientId`, since that loses the 2-policy-holder case.
   */
  triggeringClientIds: string[];
  isSurvivorship: boolean;
  possiblePrimaryClientIds: string[];
}

export interface ActiveBdgTierDecision {
  /** True when every primary recipient is deceased at trigger and the
   * cascade should fall through to the contingent tier. */
  useContingent: boolean;
  /** True when the active tier renders after the second death (survivorship
   * or trigger-client-dies-second). Drives the firstDeath/secondDeath
   * slotting in the cascade. */
  triggerFiresAtSecondDeath: boolean;
}

/**
 * Tier + slot decision for a *root* BDG entity: which tier renders, and
 * whether the cascade reads it as first-death or second-death. Shared by
 * the diagram resolver's root branch and the form-side sync hook (the
 * form only edits the root) so those two paths can't drift.
 *
 * The diagram resolver's *downstream* branch in
 * `resolveEntityNormalizedDispositions` deliberately diverges: it treats
 * both primary clients as deceased for tier availability (since downstream
 * BDGs render in the cascade's second-death column) and always slots into
 * `secondDeath`. That logic is inlined there because there's no form
 * counterpart for downstream BDGs to drift with.
 */
export function decideActiveBdgTier(
  args: DecideActiveBdgTierArgs
): ActiveBdgTierDecision {
  const deceasedAtTrigger = getDeceasedClientIdsAtTrigger({
    firstGrantorId: args.selectedPrimaryClientId,
    triggeringClientIds: args.triggeringClientIds,
    isSurvivorship: args.isSurvivorship,
    possiblePrimaryClientIds: args.possiblePrimaryClientIds,
  });
  const survivingSpouseId = getSurvivingSpouseIdAtTrigger(
    args.possiblePrimaryClientIds,
    args.selectedPrimaryClientId
  );
  const useContingent = isPrimaryTierUnavailable(
    args.primaryRecipientsForTierCheck,
    deceasedAtTrigger,
    survivingSpouseId
  );
  const triggerFiresAtSecondDeath =
    args.isSurvivorship ||
    !args.triggeringClientIds.includes(args.selectedPrimaryClientId);
  return { useContingent, triggerFiresAtSecondDeath };
}
